import {useMemo} from 'react';
import {useHalState} from '@synako/haligator';
import type {HistoriqueDevis, TypeEvenementDevis} from 'api-types/ffconnect';

// Type pour l'état HAL de l'historique des devis
type HistoriqueDevisState = ReturnType<typeof useHalState<HistoriqueDevis>>;

// Types d'événements de message
const MESSAGE_EVENT_TYPES: TypeEvenementDevis[] = [
  'message-devis.envoye',
  'commentaire-devis.envoye',
];

/**
 * Hook pour récupérer les nouveaux messages dans l'historique d'un devis
 * Le calcul est effectué côté backend pour éviter la duplication de logique
 * et permettre la réutilisation sur web et mobile
 */
export const useNouveauxMessagesDevis = (
  historiqueState: HistoriqueDevisState,
) => {
  const nombreNouveauxMessages = useMemo(() => {
    // Le backend calcule directement le nombre de nouveaux messages
    const data = historiqueState.data;
    return (data?.nouveaux_messages_count as number | undefined) ?? 0;
  }, [historiqueState.data]);

  const nombreTotalMessages = useMemo(() => {
    const historique = historiqueState.data?.historique;
    if (!historique || !Array.isArray(historique)) return 0;

    let count = 0;
    for (const event of historique) {
      if (!event || typeof event !== 'object' || !('type' in event)) {
        continue;
      }

      const eventType = event.type as TypeEvenementDevis;
      if (MESSAGE_EVENT_TYPES.includes(eventType)) {
        count++;
      }
    }

    return count;
  }, [historiqueState.data?.historique]);

  // Retourner le nombre de nouveaux messages et le total des messages
  return {
    nombreNouveauxMessages,
    nombreTotalMessages,
  };
};
